import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookletResultsPage = () => {
  const { bookletId, submissionId } = useParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmissionResults();
  }, [submissionId]);

  const fetchSubmissionResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/questions/booklets/${bookletId}/submissions`, {
        withCredentials: true
      });
      
      const foundSubmission = response.data.submissions.find(s => s.id === submissionId);
      if (foundSubmission) {
        setSubmission(foundSubmission);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      alert(t('error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-xl text-gray-600">{t('results_not_found')}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-blue-600 hover:underline"
          >
            {t('go_back')}
          </button>
        </div>
      </div>
    );
  }

  const percentage = submission.max_score > 0 
    ? Math.round((submission.total_score / submission.max_score) * 100)
    : 0;

  const getScoreColor = () => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = () => {
    if (percentage >= 80) return 'bg-green-100';
    if (percentage >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={t('booklet_results')} />

      <div className="max-w-6xl mx-auto px-4 py-8">
        
        {/* Overall Score Card */}
        <div className={`${getScoreBgColor()} rounded-2xl shadow-lg p-8 mb-8 text-center`}>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">🎉 {t('test_completed')}</h1>
          <div className={`text-6xl font-bold ${getScoreColor()} mb-2`}>
            {percentage}%
          </div>
          <p className="text-xl text-gray-700 mb-4">
            {submission.total_score} / {submission.max_score} {t('points')}
          </p>
          
          {submission.overall_comment && (
            <div className="bg-white bg-opacity-60 rounded-lg p-4 mt-4">
              <p className="text-gray-800 italic">"{submission.overall_comment}"</p>
            </div>
          )}
        </div>

        {/* Topic Mastery Summary */}
        {submission.topic_mastery_summary && submission.topic_mastery_summary.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              📊 {t('topic_mastery')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {submission.topic_mastery_summary.map((topic, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-gray-800">{topic.topic_tag}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      topic.mastery_level === 'strong' ? 'bg-green-100 text-green-800' :
                      topic.mastery_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {topic.mastery_level === 'strong' ? '💪 ' + t('strong') :
                       topic.mastery_level === 'medium' ? '📈 ' + t('medium') :
                       '📚 ' + t('needs_practice')}
                    </span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>{topic.correct} / {topic.total}</span>
                      <span>{Math.round((topic.correct / topic.total) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          topic.mastery_level === 'strong' ? 'bg-green-600' :
                          topic.mastery_level === 'medium' ? 'bg-yellow-600' :
                          'bg-red-600'
                        }`}
                        style={{ width: `${(topic.correct / topic.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  {topic.comment && (
                    <p className="text-sm text-gray-600 mt-2">{topic.comment}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per Question Results */}
        {submission.per_question_results && submission.per_question_results.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              📝 {t('detailed_feedback')}
            </h2>
            <div className="space-y-6">
              {submission.per_question_results.map((result, idx) => (
                <div
                  key={idx}
                  className={`border-l-4 ${
                    result.is_correct ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
                  } rounded-r-lg p-6`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">
                        {t('question')} {idx + 1}
                        {result.is_correct ? ' ✓' : ' ✗'}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {result.topic_tags && result.topic_tags.map((tag, i) => (
                          <span key={i} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-2xl font-bold ${result.is_correct ? 'text-green-600' : 'text-red-600'}`}>
                        {result.score} / {result.max_score}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-white bg-opacity-60 rounded-lg p-4 mb-3">
                    <p className="text-gray-800">{result.feedback}</p>
                  </div>
                  
                  {result.model_answer && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-800 mb-1">{t('model_answer')}:</p>
                      <p className="text-gray-700 text-sm">{result.model_answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            {t('back_to_dashboard')}
          </button>
          <button
            onClick={() => navigate(`/booklet/${bookletId}/take`)}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            {t('retake_test')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookletResultsPage;
